import { ticketType } from "@agentcall/core/type"
import * as React from "react"
import { ArrowRight, Home, MessageCircle } from "react-feather"
import type { TicketStatus } from "@/components/tickets/type"
import { Badge } from "@/components/ui/badge/badge"
import { cn } from "@/lib/utils"

interface TicketCardProps {
  title: string
  propertyName: string
  roomName: string
  status: TicketStatus
  createdAt: string
  onClick?: () => void
  className?: string
}

const getStatusLabel = (status: TicketStatus): string => {
  const statusItem = ticketType.TICKET_STATUSES.find((item) => item.key === status)
  return statusItem?.value || status
}

const statusConfig = {
  oc_in_progress: {
    label: getStatusLabel("oc_in_progress"),
    className: "bg-white text-slate-600 border border-slate-600",
  },
  owner_in_progress: {
    label: getStatusLabel("owner_in_progress"),
    className: "bg-[#FF3232] text-white border-0",
  },
  contractor_in_progress: {
    label: getStatusLabel("contractor_in_progress"),
    className: "bg-[#458CF7] text-white border-0",
  },
  completed: {
    label: getStatusLabel("completed"),
    className: "bg-slate-200 text-slate-600 border-0",
  },
}

export const TicketCard = React.forwardRef<HTMLButtonElement, TicketCardProps>(
  ({ title, propertyName, roomName, status, createdAt, onClick, className }, ref) => {
    const statusStyle = statusConfig[status]

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "rounded-md bg-white border border-slate-300 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors w-full text-left",
          className
        )}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <span className="text-xs font-semibold text-slate-400">{createdAt}</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Home className="w-3 h-3 text-slate-500" />
                <span className="text-sm font-semibold text-slate-600">{propertyName}</span>
              </div>
              <span className="text-sm font-medium text-slate-500">{roomName}</span>
            </div>

            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-slate-500" />
              <h3 className="text-base font-semibold text-slate-600">{title}</h3>
            </div>

            <Badge className={cn("px-2 py-1 text-xs font-bold", statusStyle.className)}>
              {statusStyle.label}
            </Badge>
          </div>

          <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-4" />
        </div>
      </button>
    )
  }
)

TicketCard.displayName = "TicketCard"
